import { Button, Flex, Spin } from "antd"
import { Alert } from "~/components/Alert"
import { formatDurationSeconds, roundToDecimals } from "~/common/utils"
import { AnalyticsAction } from "~/components/Analytics"
import { Text } from "~/components/Text"
import { VideoClipButton } from "~/components/VideoClipButton"
import styles from "./index.css"

export type VideoClipOptions = {
  duration?: number
  durationStatus: "idle" | "loading" | "error"
  start_time: number
  end_time: number
  minDuration: number
  maxDuration: number
  clipAnalyticsPrefix?: string
  onChange: (start: number, end: number) => void
  onRetry?: () => void
}

export const VideoClipControls = ({
  videoUrl,
  duration,
  durationStatus,
  start_time,
  end_time,
  minDuration,
  maxDuration,
  clipAnalyticsPrefix,
  onChange,
  onRetry,
}: VideoClipOptions & { videoUrl: string }) => {
  const hasVideo = Boolean(videoUrl.trim())
  const ready =
    hasVideo &&
    durationStatus === "idle" &&
    duration !== undefined &&
    Number.isFinite(duration) &&
    duration > 0
  const hasRange = end_time > start_time
  const clipDuration = roundToDecimals(end_time - start_time)
  const tooShort = ready && duration < minDuration
  const rangeError = !ready
    ? ""
    : tooShort
      ? `当前视频共${formatDurationSeconds(duration, "秒")}，仅支持复刻${minDuration}~${maxDuration}秒的视频片段，请重新上传要复刻的视频片段`
      : !hasRange || clipDuration < minDuration || clipDuration > maxDuration
        ? `当前视频共${formatDurationSeconds(duration, "秒")}，仅支持复刻${minDuration}~${maxDuration}秒的视频片段，请先截取要复刻的片段后再继续`
        : ""
  const clipButton = (
    <VideoClipButton
      className={styles.videoClipButton}
      size="small"
      duration={duration}
      disabled={!ready}
      videoUrl={videoUrl}
      start_time={start_time}
      end_time={end_time}
      minDuration={minDuration}
      maxDuration={maxDuration}
      onConfirm={onChange}
    />
  )

  return (
    <Flex
      vertical
      gap={12}
    >
      {hasVideo && durationStatus === "loading" ? (
        <Flex
          className={styles.videoLoading}
          align="center"
          gap={8}
        >
          <Spin size="small" />
          <Text type="secondary">正在读取视频时长…</Text>
        </Flex>
      ) : null}
      {hasVideo && durationStatus === "error" ? (
        <Alert
          level="strong"
          showIcon
          title="无法读取视频时长，请重试或重新上传"
          action={
            onRetry ? (
              <Button
                size="small"
                onClick={onRetry}
              >
                重试
              </Button>
            ) : undefined
          }
        />
      ) : null}
      {hasVideo ? (
        <Flex
          gap={8}
          align="center"
        >
          {!tooShort &&
            (clipAnalyticsPrefix ? (
              <AnalyticsAction
                action="截取视频片段"
                prefix={clipAnalyticsPrefix}
              >
                {clipButton}
              </AnalyticsAction>
            ) : (
              clipButton
            ))}
          {rangeError ? (
            <Text
              size="s"
              color="primary"
              className={styles.videoRange}
            >
              {rangeError}
            </Text>
          ) : ready && hasRange ? (
            <Text
              size="s"
              className={styles.videoRange}
              type="secondary"
            >
              已选 {formatDurationSeconds(start_time)} -{" "}
              {formatDurationSeconds(end_time)}（
              {formatDurationSeconds(clipDuration)}）
            </Text>
          ) : null}
        </Flex>
      ) : null}
    </Flex>
  )
}
